import moment from 'moment/moment';
import { has } from 'lodash';
import APIClient from '../../common/services/APIClient';
import { stepInMillisec } from '../Reports/components/DrillDownChart/actions';
import {
  getEndOfDayInSeconds,
  getTheLastNdaysInMilliseconds,
  getTheLastNdaysInSeconds,
} from '../../common/services/DateUtils';
import { sortingLowerCase } from '../../common/services/I2P2Helper';

const intervalIds = {};

const setStateStatisticsData = (data) => ({
  type: 'SET_STATE_STATISTICS_DATA',
  data,
});

const setTotalEnergyStatisticsData = (data) => ({
  type: 'SET_TOTAL_ENERGY_STATISTICS_DATA',
  data,
});

const statisticsRequest = () => ({
  type: 'STATISTICS_REQUEST',
});

const setFaultsStatisticsData = (data) => ({
  type: 'SET_FAULTS_STATISTICS_DATA',
  data,
});

const setAggregatedStatisticsData = (data) => ({
  type: 'SET_AGGREGATED_STATISTICS_DATA',
  data,
});

const setConnectionStatisticsData = (data) => ({
  type: 'SET_CONNECTION_STATISTICS_DATA',
  data,
});

const setConditionStatisticsData = (data) => ({
  type: 'SET_CONDITION_STATISTICS_DATA',
  data,
});

const intervals = {
  condition: {
    key: 'conditionIntervalId',
    request: {
      url: '/chargers/statistics/condition',
    },
    successAction: (data) => setConditionStatisticsData(data),
  },
  connection: {
    key: 'connectionIntervalId',
    request: {
      url: '/chargers/statistics/online',
    },
    successAction: (data) => setConnectionStatisticsData(data),
  },
  status: {
    key: 'statusIntervalId',
    request: {
      url: '/chargers/statistics/status',
    },
    successAction: (data) => setStateStatisticsData(data),
  },
  faults: {
    key: 'faultsIntervalId',
    request: {
      url: '/reports/faults',
      method: 'post',
      getBody: () => ({
        filter_by: {
          start_timestamp: getTheLastNdaysInSeconds(2),
          end_timestamp: getEndOfDayInSeconds(moment()),
        },
      }),
    },
    successAction: (data) =>
      setFaultsStatisticsData({
        ...data,
        legend: data.legend.sort((a, b) => sortingLowerCase(a, b, 'label')),
      }),
  },
  aggregated: {
    key: 'aggregatedIntervalId',
    request: {
      url: '/reports/sessions/aggregated',
      method: 'post',
      getBody: () => ({
        filter_by: {
          start_time: getTheLastNdaysInMilliseconds(2),
          end_time: moment().valueOf().toString(),
          step: stepInMillisec.day.toString(),
        },
      }),
    },
    successAction: (response) => {
      const data = {
        ...response,
        data: response.data.map((item) => ({
          ...item,
          date: item.date / 1000,
        })),
      };

      return setAggregatedStatisticsData(data);
    },
  },
  totalEnergyDay: {
    key: 'totalEnergyDayIntervalId',
    request: {
      url: '/reports/total-consumed-energy',
      method: 'post',
      getBody: () => ({
        fromTs: getTheLastNdaysInMilliseconds(0),
        toTs: moment().valueOf().toString(),
      }),
    },
    successAction: (data) => setTotalEnergyStatisticsData({ day: data }),
  },
  totalEnergyForWeek: {
    key: 'totalEnergyForWeekIntervalId',
    request: {
      url: '/reports/total-consumed-energy',
      method: 'post',
      getBody: () => ({
        fromTs: getTheLastNdaysInMilliseconds(6),
        toTs: moment().valueOf().toString(),
      }),
    },
    successAction: (data) => setTotalEnergyStatisticsData({ week: data }),
  },
  totalEnergyForMonth: {
    key: 'totalEnergyForMonthIntervalId',
    request: {
      url: '/reports/total-consumed-energy',
      method: 'post',
      getBody: () => ({
        fromTs: getTheLastNdaysInMilliseconds(29),
        toTs: moment().valueOf().toString(),
      }),
    },
    successAction: (data) => setTotalEnergyStatisticsData({ month: data }),
  },
};

const getStatistics = (request, successAction) => (dispatch) => {
  dispatch(statisticsRequest());

  const requestBody = has(request, 'getBody') ? request.getBody() : request.body

  switch (request.method) {
    case 'post': {
      APIClient.post(request.url, requestBody)
        .then((resp) => {
          dispatch(successAction(resp.data));
        })
        .catch((error) => {
          console.log(error);
        });

      break;
    }

    default: {
      APIClient.get(request.url, requestBody && requestBody)
        .then((resp) => {
          dispatch(successAction(resp.data.statistics));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }
};

const setStatisticInterval = (key, request, successAction) => (dispatch) => {
  dispatch(getStatistics(request, successAction));

  const intervalId = setInterval(() => {
    dispatch(getStatistics(request, successAction));
  }, 10000);

  intervalIds[key] = intervalId;
};

export const watchStatistics = () => (dispatch) => {
  for (const key in intervals) {
    const stat = intervals[key];

    dispatch(setStatisticInterval(stat.key, stat.request, stat.successAction));
  }
};

export const stopWatchStatistics = () => (dispatch) => {
  for (const key in intervals) {
    const intervalKey = intervals[key].key;
    const intervalId = intervalIds[intervalKey];

    clearInterval(intervalId);
    delete intervalIds[intervalKey];
  }
};
